import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Circle, Check, Trash2 } from 'lucide-react'
import { useCaseStore } from '../../stores'
import { useI18n } from '../../i18n'
import { isDescribeLocked } from '../../utils/describeLock'
import { aiAPI } from '../../services/api'
import { augmentInFlight, startAugment } from '../../utils/augmentPrefetch'
import { ChatKeyframes, CompactHeader, OrellaChatBubble } from '../../components/OrellaChatHeader'
import OrellaThinkingBubble from '../../components/OrellaThinkingBubble'
import DeleteCaseModal from '../../components/DeleteCaseModal'
import { isHeldByDoctor, reportDeleteFailure } from '../../utils/caseDelete'
import SubmitOverlay from '../../components/SubmitOverlay'
import { OrellaSpinner } from '../../components/OrellaLoader' // s123: the app's one spinner

// ═══════════════════════════════════════════════════════
// DescribeAdaptive v161.0.0 — Stage 2 AI Adaptive Questions
// Renders 3-5 AI-generated follow-up questions between
// Additional Factors and Review.
//
// DESIGN: Uses the exact same card/button/bottom-bar
// patterns as DescribeSection so the patient experiences
// a seamless continuation of the survey flow.
// ═══════════════════════════════════════════════════════

export default function DescribeAdaptive() {
  const navigate = useNavigate()
  const { currentCase, updateCase, updateAndSaveCase, deleteCase } = useCaseStore()
  const { t, language } = useI18n()
  // Safe translation: if t() returns the raw key (lookup failed), use fallback instead
  const ts = (key, fallback) => { const v = t(key); return (!v || v === key) ? fallback : v }
  
  const [questions, setQuestions] = useState(null)
  const [answers, setAnswers] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showContent, setShowContent] = useState(() => {
    return !!sessionStorage.getItem(`orella_chat_adaptive_${currentCase?.id}`)
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [saving, setSaving] = useState(false)
  // v214.9.0 (Bug 5): full-screen overlay during save+navigate so the "Review My
  // Answers" button doesn't appear to vanish during the route fade.
  const [submitPhase, setSubmitPhase] = useState(null)
  
  const fetchedRef = useRef(false)
  // v242.235.0: answers lock once the summary is written (utils/describeLock.js owns the rule).
  const isLocked = isDescribeLocked(currentCase)

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // ─── Load adaptive questions ───
  useEffect(() => {
    if (!currentCase?.id || fetchedRef.current) return
    fetchedRef.current = true

    // v213.9 (Sprint 13C-A2): For 'general' specialty cases, this screen renders the
    // additional_factors AUGMENT (the 4th per-category augmentation pass), NOT a
    // separate adaptive call. The general path: 12 anchor + 12 augment ≈ 24 Qs total
    // — no additional adaptive pass on top. We pull the augment result from
    // sessionStorage (pre-warmed by DescribeSection's handleNext on the additional
    // step) or call /ai/augment-category if the cache missed.
    // v229.0.0: also use per-category augmentation for canonical-specialty general-concern cards.
    const isGeneral = (currentCase?.specialtyId || '').toLowerCase() === 'general'
      || (currentCase?.focusAreaId || '').toLowerCase().endsWith('-general')
    if (isGeneral) {
      const augKey = `orella_augment_${currentCase.id}_additional_factors`
      const inflightKey = `orella_augment_inflight_${currentCase.id}_additional_factors`

      const tryLoadFromCache = () => {
        const cached = sessionStorage.getItem(augKey)
        if (cached) {
          try {
            const parsed = JSON.parse(cached)
            if (Array.isArray(parsed?.questions) && parsed.questions.length > 0) {
              setQuestions(parsed.questions)
              setLoading(false)
              const existing = currentCase?.adaptiveAnswers || currentCase?.adaptive_answers
              if (existing && typeof existing === 'object') setAnswers(existing)
              return true
            }
          } catch (e) { /* fall through */ }
        }
        return false
      }

      if (tryLoadFromCache()) return

      // PROD-1227: join the call DescribeSection's handleNext started on the Additional screen
      // (it used to be polled for once a second, after a fixed 800 ms delay). No result, or a
      // failed call: one fresh attempt, which skips to the summary when it fails too.
      const pending = augmentInFlight(currentCase.id, 'additional_factors')
      if (pending) {
        pending.then(() => { if (!tryLoadFromCache()) fetchAugmentFresh() })
        return
      }

      // PROD-1227 (review L3): no running call. The module's registry is the only authority on
      // one; an in-flight marker without it was left by an earlier page load (a reload, or an iOS
      // webview restored), and nothing in this page load can fill its cache. Polling it held the
      // patient on the loader for 45 s. Drop the marker and fetch now.
      try { sessionStorage.removeItem(inflightKey) } catch (_) { /* sessionStorage unavailable */ }
      fetchAugmentFresh()
      return
    }

    // 1. Check Zustand cache (from DescribeSection prefetch → updateCase)
    const cached = currentCase?.adaptiveQuestions || currentCase?.adaptive_questions
    if (cached?.questions?.length >= 2) {
      console.log('[DescribeAdaptive] Using cached adaptive questions:', cached.questions.length)
      setQuestions(cached.questions)
      setLoading(false)
      const existing = currentCase?.adaptiveAnswers || currentCase?.adaptive_answers
      if (existing && typeof existing === 'object') setAnswers(existing)
      return
    }
    
    // 2. Check sessionStorage (result from DescribeSection prefetch)
    const cachedResult = sessionStorage.getItem(`orella_adaptive_result_${currentCase.id}`)
    if (cachedResult) {
      try {
        const parsed = JSON.parse(cachedResult)
        if (parsed?.questions?.length >= 2) {
          console.log('[DescribeAdaptive] Using sessionStorage cached result:', parsed.questions.length)
          setQuestions(parsed.questions)
          updateCase({ adaptiveQuestions: parsed, adaptive_questions: parsed })
          setLoading(false)
          sessionStorage.removeItem(`orella_adaptive_result_${currentCase.id}`)
          return
        }
      } catch (e) { /* fall through */ }
    }
    
    // 3. Poll for in-flight prefetch
    const inflightKey = `orella_adaptive_inflight_${currentCase.id}`
    if (sessionStorage.getItem(inflightKey)) {
      console.log('[DescribeAdaptive] Prefetch in-flight, polling...')
      let polls = 0
      const pollInterval = setInterval(() => {
        polls++
        const result = sessionStorage.getItem(`orella_adaptive_result_${currentCase.id}`)
        if (result) {
          clearInterval(pollInterval)
          try {
            const parsed = JSON.parse(result)
            if (parsed?.questions?.length >= 2) {
              setQuestions(parsed.questions)
              updateCase({ adaptiveQuestions: parsed, adaptive_questions: parsed })
              setLoading(false)
              sessionStorage.removeItem(`orella_adaptive_result_${currentCase.id}`)
              sessionStorage.removeItem(inflightKey)
              return
            }
          } catch (e) { /* fall through to fresh fetch */ }
        }
        if (polls > 45) {
          clearInterval(pollInterval)
          sessionStorage.removeItem(inflightKey)
          fetchFresh()
        }
      }, 1000)
      return () => clearInterval(pollInterval)
    }
    
    // 4. No cache — fetch fresh
    fetchFresh()
  }, [currentCase?.id])

  // v213.9 (Sprint 13C-A2): Fetch the additional_factors augment for general cases.
  // Mirrors fetchFresh() but calls augmentCategory instead of generateAdaptiveQuestions.
  const fetchAugmentFresh = useCallback(async () => {
    if (!currentCase?.id) return
    console.log('[DescribeAdaptive] Fetching additional_factors augment for general case:', currentCase.id.slice(-8))
    setLoading(true)
    setError(null)
    try {
      // v226.10.0: the augment questions already shown in earlier categories (details/signs/
      // context) go as priorQuestions so the final additional_factors augment dedups against
      // them; PROD-1227: startAugment builds that list, joins a running call, caches the result
      // and resolves null when the call fails.
      const result = await startAugment({ caseId: currentCase.id, category: 'additional_factors', language, fetchAugment: aiAPI.augmentCategory })
      if (Array.isArray(result?.questions) && result.questions.length > 0) {
        setQuestions(result.questions)
        console.log('[DescribeAdaptive] ✅ Loaded', result.questions.length, 'augment Qs')
      } else {
        console.log('[DescribeAdaptive] Augment returned no Qs, skipping to review')
        setError('skip')
      }
    } catch (err) {
      console.error('[DescribeAdaptive] Augment fetch failed:', err?.message)
      // For general specialty, if augmentation fails completely, skip to review
      // rather than blocking the user. The case still has 12 anchor answers.
      setError('skip')
    } finally {
      setLoading(false)
    }
  }, [currentCase?.id, language])

  const fetchFresh = useCallback(async () => {
    if (!currentCase?.id) return
    console.log('[DescribeAdaptive] Fetching adaptive questions fresh for case:', currentCase.id.slice(-8))
    setLoading(true)
    setError(null)
    try {
      const result = await aiAPI.generateAdaptiveQuestions(currentCase.id, language)
      if (result?.questions?.length >= 2) {
        setQuestions(result.questions)
        updateCase({ adaptiveQuestions: result, adaptive_questions: result })
        console.log('[DescribeAdaptive] ✅ Loaded', result.questions.length, 'adaptive questions')
      } else {
        console.log('[DescribeAdaptive] AI returned insufficient questions, skipping to review')
        setError('skip')
      }
    } catch (err) {
      console.error('[DescribeAdaptive] Failed to generate questions:', err?.message)
      setError(err?.message || t('errors2.failedToGenerateQuestions'))
    } finally {
      setLoading(false)
    }
  }, [currentCase?.id, language])

  // Auto-skip to review if no questions available
  useEffect(() => {
    if (!loading && error === 'skip') {
      navigate('/case/describe/review', { replace: true })
    }
  }, [loading, error])

  // ─── Answer handlers (same pattern as DescribeSection) ───
  const handleSingleSelect = (questionId, option) => {
    if (isLocked) return
    setAnswers(prev => ({
      ...prev,
      [questionId]: prev[questionId] === option ? '' : option
    }))
  }

  const handleFreeText = (questionId, value) => {
    if (isLocked) return
    setAnswers(prev => ({ ...prev, [questionId]: value }))
  }

  // v214.9.0 (Bug 3): multi-select support — answers stored as an array of
  // selected option strings. Mirrors DescribeSection's "None of these" logic:
  // selecting None clears others; selecting a regular option clears None.
  const isNoneOption = (option) => {
    const lower = (option || '').toLowerCase()
    return lower.includes('none of these') ||
           lower.includes('none of the above') ||
           lower === 'none' ||
           lower === 'not sure' ||
           lower.includes('no symptoms') ||
           lower.includes('not experiencing any')
  }

  const handleMultiSelect = (questionId, option) => {
    if (isLocked) return
    setAnswers(prev => {
      const current = Array.isArray(prev[questionId]) ? prev[questionId] : []
      let next
      if (current.includes(option)) {
        next = current.filter(o => o !== option)
      } else if (isNoneOption(option)) {
        next = [option]
      } else {
        next = [...current.filter(o => !isNoneOption(o)), option]
      }
      return { ...prev, [questionId]: next }
    })
  }

  const getMultiAnswers = (questionId) => {
    const v = answers[questionId]
    return Array.isArray(v) ? v : []
  }

  // ─── Navigation ───
  const handleContinue = async () => {
    setSaving(true)
    // v214.9.0 (Bug 5): show full-screen overlay so the button doesn't appear
    // to vanish during the route fade. Stays up until the next page mounts.
    setSubmitPhase('processing')

    // Track save start time so we can guarantee minimum spinner visibility
    const saveStart = Date.now()

    try {
      if (currentCase?.id && Object.keys(answers).length > 0) {
        await updateAndSaveCase({ adaptiveAnswers: answers })
        console.log('[DescribeAdaptive] Saved', Object.keys(answers).length, 'adaptive answers')
      }

      // ★★ v242.235.0 (session 96) — THE EARLY SUMMARY LANE THAT LIVED HERE IS GONE. It fired
      //   generatePatientSummary 1.5 s after Continue while ViewSummary streamed its own summary,
      //   and the slower of the two overwrote the store (and, via its own save, the column) after
      //   the patient had read the other one. It was also redundant: the server already prewarms
      //   the summary when these adaptive answers land (update_case, SUMMARY-GW-TIMEOUT-1).
      //   Dr. Rami's ruling: the summary is written ONCE — final from the "View my summary" tap
      //   on Review (utils/describeLock.js; orella-api _patient_summary_final).
    } finally {
      // v214.9.0 (Bug 5): keep overlay up for at least 700ms so the patient sees
      // clear feedback ("we're saving"), then navigate. The overlay component
      // unmounts naturally when this screen unmounts after navigation.
      const elapsed = Date.now() - saveStart
      const remaining = Math.max(0, 700 - elapsed)
      setTimeout(() => {
        navigate('/case/describe/review')
      }, remaining)
    }
  }

  const handleSkipToReview = () => {
    // v213.3 (C-A-189): Skip jumps straight to the View Summary page (where the
    // patient submits) instead of the intermediate Review section list.
    navigate('/case/describe/summary')
  }

  const handleDeleteCase = async () => {
    // DELETE-CURRENTCASE-NULL-1 — NAVIGATE FIRST, DELETE SECOND.
    // deleteCase() sets currentCase to null in the store. Awaiting it here re-rendered
    // THIS screen against that null while it was still mounted, and the first thing in
    // render that reads currentCase.<x> without a ?. threw a React error. The delete and
    // the navigation both completed, which is exactly why it looked cosmetic: the app
    // "worked" and threw anyway. Unmounting first removes the whole class, at every one
    // of the six screens that can delete their own case, rather than optional-chaining
    // whichever line happened to fire (RULES §1bv(a)).
    const id = currentCase?.id
    setShowDeleteModal(false)
    navigate('/home', { replace: true })
    // ★ PROD-416: a delete the server refuses leaves the case, and the patient is told.
    if (id) { try { await deleteCase(id) } catch (err) { reportDeleteFailure(t, err) } }
  }

  const handleSaveAsDraft = async () => {
    if (currentCase?.id) {
      await updateAndSaveCase({ status: 'draft', adaptiveAnswers: answers })
    }
    setShowDeleteModal(false)
    navigate('/cases', { replace: true })
  }

  if (!currentCase) {
    navigate('/home')
    return null
  }

  // v214.9.0: handle arrays from multi-select questions in addition to strings
  const hasAnyAnswers = Object.entries(answers).some(([, v]) => {
    if (Array.isArray(v)) return v.length > 0
    if (typeof v === 'string') return v.trim().length > 0
    return !!v
  })

  return (
    <div className="bg-white flex flex-col relative min-h-full">
      <ChatKeyframes />

      {/* Compact Header — matches DescribeSection exactly */}
      <CompactHeader
        stepNum={6}
        totalSteps={7}
        caseInfo={`${currentCase.specialtyName} › ${currentCase.focusAreaName}`}
        statusLabel={currentCase.status === 'finalized' ? t('caseHome.statusFinalized') : 
          currentCase.status === 'claimed' ? t('caseHome.statusClaimed') :
          currentCase.status === 'shared' ? t('caseHome.statusShared') :
          currentCase.status === 'submitted' ? t('cases.statusSubmitted') : t('caseHome.statusInProgress')}
        onBack={() => navigate('/case/describe/additional')}
        onNotification={() => navigate('/notifications')}
      />

      {/* ─── Loading State ─── */}
      {loading && (
        <>
          {/* v230.2.3 — Replaced the plain chat bubble + centered Loader2 spinner
              with the canonical OrellaThinkingBubble (avatar + woven spinning
              ring + typing dots + sub-copy), matching the Describe-extraction /
              ViewSummary loaders so the adaptive wait reads as a beat in the
              same conversation instead of a generic spinner. */}
          <div className="px-4 pt-4" style={{ animation: 'fadeSlideUp 0.3s ease' }}>
            <OrellaThinkingBubble
              message={ts('describe.chatAdaptiveLoadingTitle', 'Thinking about your responses…')}
              subMessage={ts('describe.chatAdaptiveLoadingSub', 'Putting together a few follow-up questions — just a moment.')}
            />
          </div>
          {/* v242.64.0 (TF-10): was 15000, and the number was never measured.
              MEASURED 2026-07-31 on the reporter's own request: /ai/adaptive-questions
              returned 200 with integrationLatency=25261 ms, and the Lambda logged
              [AI_ADAPTIVE] Generated 5 adaptive questions 25.08 s after the request.
              So the escape hatch was on screen for ~10 s over work that was going to
              succeed, which is exactly what the report describes ("answers seem to
              populate shortly after skip option appears").
              30000 is not a guess either: API Gateway's integration timeout is
              30,000 ms (integration gw4v5km, TimeoutInMillis), so BEFORE 30 s the
              request can still land and offering to abandon it is premature; AT 30 s
              it has either landed or been killed at the front door, and the error
              branch below takes over. Show the hatch only once waiting is futile. */}
          <SkipAfterDelay delayMs={30000}>
            <div className="px-6 pb-6 mt-2" style={{ animation: 'fadeUp 0.3s ease' }}>
              <button
                onClick={handleSkipToReview}
                className="w-full text-center text-[12px] text-gray-400 font-medium py-2"
              >
                {ts('describe.skipToReview', 'Skip to review →')}
              </button>
            </div>
          </SkipAfterDelay>
        </>
      )}

      {/* ─── Error State (non-skip) ─── */}
      {!loading && error && error !== 'skip' && (
        <>
          <OrellaChatBubble
            message={ts('describe.chatAdaptiveError', "I wasn't able to generate follow-up questions right now — no worries, you can continue to review your answers.")}
            animationKey={`adaptive_error_${currentCase?.id}`}
            onComplete={() => setShowContent(true)}
          />
          <div className="flex-1" />
          <div className="p-4 pb-7 border-t border-gray-100 flex-shrink-0 bg-white">
            <div className="flex items-center gap-2.5">
              <button
                onClick={() => setShowDeleteModal(true)}
                className="w-[42px] h-[42px] rounded-full border-[1.5px] border-red-100 flex items-center justify-center flex-shrink-0 active:bg-red-50 transition-colors"
              >
                <Trash2 size={16} className="text-red-400" />
              </button>
              <button
                onClick={handleSkipToReview}
                className="flex-1 py-3 rounded-full bg-orella-500 text-white font-bold text-[15px] active:bg-orella-600 transition-colors breathe-blue"
              >
                {ts('describe.reviewMyAnswers', 'Review My Answers')}
              </button>
            </div>
          </div>
        </>
      )}

      {/* ─── Questions Loaded ─── */}
      {!loading && !error && questions && (
        <>
          {/* Orella Chat Bubble — same pattern as DescribeSection */}
          <OrellaChatBubble
            message={ts('describe.chatAdaptive', "A few more questions tailored to your specific situation — these help your doctor understand exactly what's going on.")}
            animationKey={`adaptive_${currentCase?.id}`}
            onComplete={() => setShowContent(true)}
          />

          {/* Content area — exact same structure as DescribeSection */}
          <div
            className="flex-1 bg-white"
            style={{
              transition: 'opacity 0.4s ease, transform 0.4s ease',
              opacity: showContent ? 1 : 0,
              transform: showContent ? 'translateY(0)' : 'translateY(12px)'
            }}
          >
            <div className="flex-1 px-4 pb-4">
              <div className="space-y-6">
                {questions.map((question, qIndex) => (
                  <div key={question.id} className="border-b border-gray-100 pb-6 last:border-0">
                    {/* Question Number and Text — same style as DescribeSection */}
                    <p className="font-medium text-gray-800 mb-3">
                      <span className="text-orella-500">{qIndex + 1}.</span> {question.text}
                    </p>

                    {/* Answer options */}
                    <div className="space-y-2">
                      {question.type === 'free-text' ? (
                        /* Free text input */
                        <textarea
                          value={answers[question.id] || ''}
                          onChange={(e) => handleFreeText(question.id, e.target.value)}
                          disabled={isLocked}
                          placeholder={ts('describe.typeResponse', 'Type your response here...')}
                          className="w-full min-h-[80px] rounded-xl border border-gray-200 px-4 py-3 text-sm text-gray-700 placeholder-gray-300 resize-y outline-none focus:border-orella-500 transition-colors"
                          style={{ fontFamily: 'inherit' }}
                        />
                      ) : question.type === 'multi-select' ? (
                        /* v214.9.0 (Bug 3): multi-select — checkboxes, "None of these" clears others */
                        (question.options || []).map((option) => {
                          const isSelected = getMultiAnswers(question.id).includes(option)
                          const isNone = isNoneOption(option)
                          return (
                            <button
                              key={option}
                              onClick={() => handleMultiSelect(question.id, option)}
                              disabled={isLocked}
                              className={`w-full p-3 rounded-xl border text-left flex items-center gap-3 transition-all ${
                                isSelected
                                  ? 'border-orella-500 bg-orella-50'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              {isNone ? (
                                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                                  isSelected ? 'border-orella-500' : 'border-gray-300'
                                }`}>
                                  {isSelected && <Circle size={10} className="text-orella-500 fill-orella-500" />}
                                </div>
                              ) : (
                                <div className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 ${
                                  isSelected ? 'bg-orella-500 border-orella-500' : 'border-gray-300'
                                }`}>
                                  {isSelected && <Check size={14} className="text-white" />}
                                </div>
                              )}
                              <span className={`text-sm ${isSelected ? 'text-orella-700' : 'text-gray-700'}`}>
                                {option}
                              </span>
                            </button>
                          )
                        })
                      ) : (
                        /* Single select — exact same radio buttons as DescribeSection */
                        (question.options || []).map((option) => {
                          const isSelected = answers[question.id] === option
                          return (
                            <button
                              key={option}
                              onClick={() => handleSingleSelect(question.id, option)}
                              disabled={isLocked}
                              className={`w-full p-3 rounded-xl border text-left flex items-center gap-3 transition-all ${
                                isSelected
                                  ? 'border-orella-500 bg-orella-50'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                                isSelected
                                  ? 'border-orella-500'
                                  : 'border-gray-300'
                              }`}>
                                {isSelected && <Circle size={10} className="text-orella-500 fill-orella-500" />}
                              </div>
                              <span className={`text-sm ${isSelected ? 'text-orella-700' : 'text-gray-700'}`}>
                                {option}
                              </span>
                            </button>
                          )
                        })
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Bottom Bar — matches "additional" step: Trash + Review My Answers */}
          <div
            className="p-4 pb-7 border-t border-gray-100 flex-shrink-0 bg-white"
            style={{
              transition: 'opacity 0.4s ease, transform 0.4s ease',
              transitionDelay: '150ms',
              opacity: showContent ? 1 : 0,
              transform: showContent ? 'translateY(0)' : 'translateY(8px)'
            }}
          >
            {isLocked ? (
              <div className="flex items-center gap-2.5">
                <button
                  onClick={() => navigate('/case/describe/review')}
                  className="flex-1 py-3 rounded-full bg-orella-500 text-white font-bold text-[15px]"
                >
                  {t('common.next') || 'Next'}
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2.5">
                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="w-[42px] h-[42px] rounded-full border-[1.5px] border-red-100 flex items-center justify-center flex-shrink-0 active:bg-red-50 transition-colors"
                >
                  <Trash2 size={16} className="text-red-400" />
                </button>
                <button
                  onClick={handleContinue}
                  disabled={saving}
                  className={`flex-1 py-3 rounded-full font-bold text-[15px] transition-colors flex items-center justify-center gap-2 ${
                    saving
                      ? 'bg-orella-400 text-white'
                      : hasAnyAnswers
                        ? 'bg-orella-500 text-white active:bg-orella-600 breathe-blue'
                        : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  {saving ? (
                    <>
                      <OrellaSpinner size={16} tone="inherit" />
                      {t('common.saving', 'Saving...')}
                    </>
                  ) : (
                    ts('describe.reviewMyAnswers', 'Review My Answers')
                  )}
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteCaseModal canDelete={!isHeldByDoctor(currentCase)}
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onDelete={handleDeleteCase}
        onSaveAsDraft={handleSaveAsDraft}
        caseName={currentCase?.focusAreaName || currentCase?.focusArea?.name || ''}
      />

      {/* v214.9.0 (Bug 5): full-screen overlay during save+navigate to /review.
          Uses processing phase only (no success/exit ceremony) — kept lightweight
          since this is a transition, not a final submission. Stays up until
          this screen unmounts after navigation.
          v214.9.1: pass all 4 props defensively in case anyone ever flips us into
          success/exit phases — and so localized strings flow through consistently. */}
      <SubmitOverlay
        phase={submitPhase}
        processingTitle={ts('describeAdaptive.savingAnswers', 'Saving your answers')}
        processingSubtitle={ts('describeAdaptive.preparingReview', 'Preparing your review')}
        successTitle={ts('describeAdaptive.savingAnswers', 'Saving your answers')}
        successSubtitle={ts('describeAdaptive.preparingReview', 'Preparing your review')}
      />
    </div>
  )
}

// ─── Delay helper: renders children after specified ms ───
function SkipAfterDelay({ delayMs = 5000, children }) {
  const [show, setShow] = useState(false)
  useEffect(() => {
    const timer = setTimeout(() => setShow(true), delayMs)
    return () => clearTimeout(timer)
  }, [delayMs])
  if (!show) return null
  return <>{children}</>
}
